'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Check, Loader2, Minus, Plus, X } from 'lucide-react';
import {
  PLANS,
  formatPrice,
  getPlan,
  getTotalPrice,
  type BillingCycle,
  type Plan,
  type PlanId,
} from '@/lib/plans';

const PLAN_ORDER: PlanId[] = ['LITE', 'STANDARD', 'PREMIUM', 'ENTERPRISE'];

export type PricingPlanForDisplay = Plan & { slug: string };

export function Pricing({
  canCheckout = false,
  defaultScreens,
  plansOverride,
  visibleIds,
  directPlans,
}: {
  canCheckout?: boolean;
  defaultScreens?: number;
  plansOverride?: Partial<Record<PlanId, Plan>>;
  visibleIds?: PlanId[];
  // New: pass full plans directly (used with PricingPlan DB records)
  directPlans?: PricingPlanForDisplay[];
}) {
  const router = useRouter();
  const [cycle, setCycle] = useState<BillingCycle>('yearly');
  const [busy, setBusy] = useState<string | null>(null);

  // Determine which plans to show
  const plans: Array<Plan & { slug?: string }> = directPlans
    ? directPlans
    : ((visibleIds && visibleIds.length > 0 ? visibleIds : PLAN_ORDER).map(
        (id) => plansOverride?.[id] ?? PLANS[id]
      ));

  async function checkout(planId: PlanId, slug: string | undefined, screens: number) {
    if (!canCheckout) {
      router.push('/register');
      return;
    }
    setBusy(slug || planId);
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: planId,
          planSlug: slug,
          billingCycle: cycle,
          screens,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.checkoutUrl) {
        alert(data.error || 'Checkout mislukt');
        setBusy(null);
        return;
      }
      window.location.href = data.checkoutUrl;
    } catch (e) {
      alert('Er ging iets mis bij het starten van de betaling.');
      setBusy(null);
    }
  }

  return (
    <section id="pricing" className="max-w-[1200px] mx-auto px-6 py-24">
      <div className="text-center mb-10">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary font-medium">
          — Tarieven
        </span>
        <h2 className="text-4xl md:text-5xl font-bold mt-4 tracking-tight">
          Start vandaag.
          <br />
          <span className="text-ink-3">7 dagen gratis op proef.</span>
        </h2>
        <p className="text-base text-ink-2 mt-5 max-w-xl mx-auto">
          Probeer Castly 7 dagen gratis met alle Pro features. Daarna kies je het plan dat bij je past — geen creditcard vooraf nodig.
        </p>
      </div>

      {/* Billing cycle toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex bg-bg-3 rounded-full p-1 border border-border">
          <button
            onClick={() => setCycle('monthly')}
            className={`px-5 py-2 text-sm rounded-full font-medium transition-colors ${
              cycle === 'monthly' ? 'bg-white shadow-sm' : 'text-ink-2'
            }`}
          >
            Per maand
          </button>
          <button
            onClick={() => setCycle('yearly')}
            className={`px-5 py-2 text-sm rounded-full font-medium transition-colors flex items-center gap-2 ${
              cycle === 'yearly' ? 'bg-white shadow-sm' : 'text-ink-2'
            }`}
          >
            Per jaar
            <span className="text-[10px] bg-primary text-white px-1.5 py-0.5 rounded font-bold">
              -25%
            </span>
          </button>
        </div>
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 ${plans.length >= 3 ? 'lg:grid-cols-3' : ''} ${plans.length >= 4 ? 'lg:grid-cols-4' : ''} gap-4 max-w-6xl mx-auto`}>
        {plans.map((plan) => (
          <PlanCard
            key={plan.slug ?? plan.id}
            plan={plan}
            cycle={cycle}
            isBusy={busy === (plan.slug ?? plan.id)}
            onCheckout={(screens) => checkout(plan.id, plan.slug, screens)}
            canCheckout={canCheckout}
            defaultScreens={defaultScreens}
          />
        ))}
      </div>

      <p className="text-center text-xs text-ink-3 mt-8">
        Alle prijzen excl. 21% btw · Maandelijks opzegbaar · iDEAL, creditcard, en SEPA · Betalingen via Mollie
      </p>
    </section>
  );
}

function PlanCard({
  plan,
  cycle,
  isBusy,
  onCheckout,
  canCheckout,
  defaultScreens,
}: {
  plan: Plan;
  cycle: BillingCycle;
  isBusy: boolean;
  onCheckout: (screens: number) => void;
  canCheckout: boolean;
  defaultScreens?: number;
}) {
  const isFree = plan.id === 'FREE';
  const isEnterprise = plan.id === 'ENTERPRISE';
  const isLite = plan.id === 'LITE';
  const isPaid = !isFree && !isEnterprise;

  // Default screen count = pre-filled value clamped to plan range, or minimum
  // LITE is locked to 1 screen
  const minScreens = isFree || isLite ? 1 : 2;
  const maxScreens = plan.maxScreens > 0 ? plan.maxScreens : 999;
  const initialScreens = isLite
    ? 1
    : defaultScreens
      ? Math.max(minScreens, Math.min(maxScreens, defaultScreens))
      : minScreens;
  const [screens, setScreens] = useState(initialScreens);
  const [showWatermarkPreview, setShowWatermarkPreview] = useState(false);

  // LITE only has yearly billing; force display value
  const effectivePerScreen = isLite
    ? plan.pricePerScreenYearly
    : (cycle === 'yearly' ? plan.pricePerScreenYearly : plan.pricePerScreenMonth);
  const perScreen = effectivePerScreen;
  const totalMonthlyEquiv =
    perScreen !== null ? perScreen * screens : null;
  const totalForCycle = getTotalPrice(plan, screens, isLite ? 'yearly' : cycle);

  return (
    <div
      className={`card p-6 relative flex flex-col ${plan.highlight ? 'ring-2 ring-primary' : ''}`}
    >
      {plan.highlight && (
        <div className="absolute -top-2.5 left-6 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
          Populair
        </div>
      )}
      <div
        className={`font-mono text-[10px] uppercase tracking-widest mb-1 ${
          plan.highlight ? 'text-primary font-bold' : 'text-ink-3'
        }`}
      >
        {plan.name}
      </div>
      <p className="text-[13px] text-ink-2 mb-5 leading-relaxed min-h-[2.5em]">
        {plan.description}
      </p>

      <div className="mb-4 min-h-[80px]">
        {isLite && plan.pricePerScreenYearly !== null ? (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold tracking-tight">
                {formatPrice(plan.pricePerScreenYearly)}
              </span>
              <span className="text-ink-3 text-sm">/mnd</span>
            </div>
            <div className="text-[11px] text-ink-3 mt-0.5">
              jaarlijks gefactureerd · {formatPrice(plan.pricePerScreenYearly * 12)}/jaar
            </div>
          </>
        ) : perScreen !== null ? (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold tracking-tight">
                {formatPrice(perScreen)}
              </span>
              <span className="text-ink-3 text-sm">/scherm/mnd</span>
            </div>
            {perScreen > 0 && (
              <div className="text-[11px] text-ink-3 mt-0.5">
                excl. btw · incl. {formatPrice(perScreen * 1.21)}
              </div>
            )}
          </>
        ) : (
          <div className="text-3xl font-bold tracking-tight">Op maat</div>
        )}
      </div>

      {/* Screen count selector for paid plans (not LITE) */}
      {isPaid && !isLite && (
        <div className="mb-4 p-3 bg-bg-3 rounded-lg">
          <label className="text-[10px] font-mono uppercase tracking-widest text-ink-3 block mb-1.5">
            Aantal schermen
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setScreens(Math.max(minScreens, screens - 1))}
              disabled={screens <= minScreens}
              className="w-7 h-7 rounded border border-border flex items-center justify-center hover:bg-bg disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Minus size={12} />
            </button>
            <input
              type="number"
              min={minScreens}
              max={maxScreens}
              value={screens}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!isNaN(n)) setScreens(Math.max(minScreens, Math.min(maxScreens, n)));
              }}
              className="flex-1 text-center font-bold text-base bg-transparent border-0 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setScreens(Math.min(maxScreens, screens + 1))}
              disabled={screens >= maxScreens}
              className="w-7 h-7 rounded border border-border flex items-center justify-center hover:bg-bg disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Plus size={12} />
            </button>
          </div>
          {totalForCycle !== null && totalMonthlyEquiv !== null && (
            <div className="mt-2 pt-2 border-t border-border">
              <div className="flex justify-between items-baseline">
                <span className="text-[11px] text-ink-3">Totaal</span>
                <span className="font-bold">
                  {formatPrice(totalMonthlyEquiv)}
                  <span className="text-[11px] text-ink-3 font-normal">/mnd</span>
                </span>
              </div>
              {cycle === 'yearly' && (
                <div className="text-[10px] text-ink-3 text-right">
                  {formatPrice(totalForCycle)} per jaar
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isFree ? (
        <Link href="/register" className="w-full text-center btn-ghost">
          {plan.cta}
        </Link>
      ) : isEnterprise ? (
        <Link href="mailto:sales@castly.nl" className="w-full text-center btn-ghost">
          {plan.cta}
        </Link>
      ) : (
        <button
          onClick={() => onCheckout(screens)}
          disabled={isBusy}
          className={`w-full ${plan.highlight ? 'btn-primary' : 'btn-ghost'}`}
        >
          {isBusy ? <Loader2 size={14} className="animate-spin" /> : plan.cta}
        </button>
      )}

      <div className="border-t border-border my-5" />
      <ul className="space-y-2 text-[13px] flex-1">
        {plan.features.map((feature) => {
          const isWatermarkFeature = isLite && feature.toLowerCase().includes('logo');
          return (
            <li key={feature} className="flex gap-2 items-start">
              <Check size={13} className="text-primary shrink-0 mt-0.5" strokeWidth={2.5} />
              <span className="flex-1">
                {feature}
                {isWatermarkFeature && (
                  <button
                    type="button"
                    onClick={() => setShowWatermarkPreview(true)}
                    className="inline-flex items-center justify-center w-4 h-4 ml-1 rounded-full bg-bg-3 hover:bg-primary-soft text-ink-3 hover:text-primary-deep text-[10px] font-bold align-middle transition-colors"
                    title="Bekijk voorbeeld"
                    aria-label="Voorbeeld watermerk"
                  >
                    i
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {showWatermarkPreview && isLite && (
        <WatermarkPreviewModal onClose={() => setShowWatermarkPreview(false)} />
      )}
    </div>
  );
}

function WatermarkPreviewModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-2 rounded-xl border border-border max-w-lg w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="font-bold">Voorbeeld watermerk</h3>
            <p className="text-[11px] text-ink-3">Zo ziet het Castly logo eruit op je scherm</p>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink p-1">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 bg-bg-3">
          {/* Mock TV screen with watermark */}
          <div className="aspect-video bg-[#1a1d24] rounded-lg overflow-hidden relative shadow-lg">
            {/* Fake content layers */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white text-center">
                <div className="font-bold text-lg mb-1">Welkom in onze zaak!</div>
                <div className="text-white/60 text-xs">Voorbeeld presentatie</div>
              </div>
            </div>
            {/* Mock weather widget top-right */}
            <div className="absolute top-3 right-3 bg-white/10 backdrop-blur rounded-md px-2 py-1 text-white text-[10px]">
              <div className="font-bold">18°C</div>
              <div className="opacity-70">Bewolkt</div>
            </div>
            {/* The watermark - bottom-right corner */}
            <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm rounded px-2 py-1 flex items-center gap-1">
              <span className="font-mono text-[8px] text-white/85 uppercase tracking-widest">via</span>
              <span className="font-bold text-[11px] text-white">Castly</span>
            </div>
          </div>
        </div>
        <div className="p-4 text-[12px] text-ink-2 leading-relaxed bg-bg-2">
          <p className="mb-2">
            <strong className="text-ink-1">Klein logo, klikbaar.</strong> Het Castly logo verschijnt rechtsonder op het scherm
            (ongeveer 1% van het schermoppervlak). Klanten kunnen erop klikken voor meer info over Castly.
          </p>
          <p className="text-ink-3">
            Wil je geen logo? Upgrade naar het Standaard plan voor schermen zonder watermerk.
          </p>
        </div>
      </div>
    </div>
  );
}
